import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/AuthService';
import { ROUTE_HOME, ENABLE_NEW_UI } from '../constants';

export const validateUser = createAsyncThunk(
  'auth/validateUser',
  async () => {
    const user = await authService.validateUser();
    return user;
  }
);

const initialState = {
  user: null,
  status: 'idle',
  error: null,
  isTrialBannerDismissed: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login(state, action) {
      state.user = action.payload;
    },
    updateUser(state, action) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    logout(state) {
      state.user = null;
      state.isTrialBannerDismissed = false;
    },
    dismissTrialBanner(state) {
      state.isTrialBannerDismissed = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(validateUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(validateUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        state.user = null;
        state.isTrialBannerDismissed = false;
      });
  }
});

export const { login, updateUser, logout, dismissTrialBanner } = authSlice.actions;

export const loginUser = (credentials, navigate, onAuthResponse) => async (dispatch) => {
  try {
    const response = await authService.login(credentials);
    if (onAuthResponse && onAuthResponse(response)) {
      return response;
    }
    await dispatch(validateUser());
    navigate(ENABLE_NEW_UI ? '/dashboard' : ROUTE_HOME);
    return response;
  }
  catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const loginUserWithGoogle = (codeResponse, navigate, onAuthResponse, turnstileToken) => async (dispatch) => {
  try {
    const response = await authService.loginWithGoogle(codeResponse.code, turnstileToken);
    if (onAuthResponse && onAuthResponse(response)) {
      return response;
    }

    if (response && response.action === 'complete_registration') {
      navigate(`/register/firmoauth?code=${response.code}`, {
        state: {
          firstName: response.firstName,
          lastName: response.lastName,
        },
      });
      return response;
    } 

    await dispatch(validateUser());
    navigate(ENABLE_NEW_UI ? '/dashboard' : ROUTE_HOME);
    return response;
  }
  catch (error) {
    console.error('Google login failed:', error);
    throw error;
  }
};

export const verifyTwoFactor = (payload, navigate) => async (dispatch) => {
  try {
    const response = await authService.verifyTwoFactor(payload);
    await dispatch(validateUser());
    navigate(ENABLE_NEW_UI ? '/dashboard' : ROUTE_HOME);
    return response;
  } catch (error) {
    console.error('2FA Verification failed:', error);
    throw error;
  }
};

export const logoutUser = (navigate) => async (dispatch) => {
  await authService.logout(navigate);
  dispatch(logout());
};

export default authSlice.reducer;